import { applyMiddleware, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import pick from 'lodash/pick';
import truncate from 'lodash/truncate';
import isObject from 'lodash/isObject';
import isNil from 'lodash/isNil';
import isFunction from 'lodash/isFunction';
import omit from 'lodash/omit';
import isString from 'lodash/isString';
import cloneDeep from 'lodash/cloneDeep';

import { createRootReducer } from './store/reducers';

function logPathChange() {
    return next => action => {
        if (action.type === '@@reduxReactRouter/routerDidChange') {
            ReactGA._gaCommandSendPageview(action.payload.location.pathname, {
                title: action.payload.location.pathname,
                page: action.payload.location.pathname
            });
        }
        return next(action);
    };
}

export const SENTRY_MAX_EXTRA_CHARACTER_COUNT = 100000;
export const SENTRY_EXTRA_CHARACTER_BUFFER = 100;

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
    actionTransformer: action => {
        const actionCopy = {
            type: action.type
        };
        if (!isNil(action.payload)) {
            const payload = action.payload;
            if (isObject(payload)) {
                if (JSON.stringify(payload).length < SENTRY_MAX_EXTRA_CHARACTER_COUNT) {
                    actionCopy.payload = cloneDeep(action.payload);
                }
            } else if (isString(payload)) {
                actionCopy.payload = truncate(actionCopy.payload, {
                    length: SENTRY_MAX_EXTRA_CHARACTER_COUNT - SENTRY_EXTRA_CHARACTER_BUFFER
                });
            }
        }
        return actionCopy;
    },
    stateTransformer: state => {
        return {
            ...pick(state, ['authentication.spoc']),
            tokenParsed: omit(state?.authentication?.kc?.tokenParsed ?? {}, [
                'allowed-origins',
                'realm_access',
                'resource_access',
                'siteIds'
            ])
        };
    },
    configureScopeWithState(scope, state) {
        scope.setTag('email', state?.authentication?.kc?.tokenParsed?.email);
        scope.setTag('username', state?.authentication?.kc?.tokenParsed?.preferred_username);
        scope.setTag('name', state?.authentication?.kc?.tokenParsed?.name);
        scope.setTag('spoc', state?.authentication?.spoc?.id);
    }
});

const middlewarez = [applyMiddleware(thunk), applyMiddleware(logPathChange), sentryReduxEnhancer];

let composeEnhancers = compose;
if (process.env.NODE_ENV !== 'production') {
    /* eslint-disable no-underscore-dangle */
    if (isFunction(window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__)) {
        composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ trace: true });
    }
    /* eslint-enable */
}

const finalCreateStore = composeEnhancers(...middlewarez)(createStore);

export const initStore = initialState => finalCreateStore(createRootReducer(), initialState);
